<template>
  <Transition name="modal" appear>
    <div
      v-if="modalStore.isOpen"
      class="fixed z-50 inset-0 overflow-y-auto bg-black bg-opacity-50"
      @click="handleBackdropClick"
    >
      <div class="flex min-h-screen items-center justify-center p-4">
        <Transition name="modal-content" appear>
          <div
            v-if="modalStore.component"
            ref="modalContent"
            class="relative max-h-[90vh] w-full overflow-y-auto bg-white rounded-lg shadow-xl"
            :class="modalWidthClass"
            @click.stop
          >
            <component
              :is="modalStore.component"
              v-bind="modalStore.props"
              @close="closeModal"
            />
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useModalStore } from "@/store/modal";

export default {
  name: "ModalWrapper",
  props: {
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const modalStore = useModalStore();
    const modalContent = ref(null);
    let historyState = null;

    const modalWidthClass = computed(() => {
      const customWidth = modalStore.props?.modalWidth;

      const widthMap = {
        "400px": "md:w-96",
        "500px": "md:w-[500px]",
        "600px": "md:w-[600px]",
        "700px": "md:w-[700px]",
        "800px": "md:w-[800px]",
        "900px": "md:w-[900px]",
        "1000px": "md:w-[1000px]",
        "1200px": "md:w-[1200px]",
      };

      return widthMap[customWidth] || "md:w-[500px]";
    });

    const handlePopState = (e) => {
      // If modal is open and we detect a back navigation, close the modal
      if (modalStore.isOpen) {
        e.preventDefault();
        closeModal();
        // Push the current state back to prevent actual page navigation
        window.history.pushState(historyState, '', window.location.href);
      }
    };

    const pushModalState = () => {
      // Store current history state
      historyState = window.history.state;
      // Push a new state when modal opens
      window.history.pushState({ modal: true }, '', window.location.href);
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    const handleBackdropClick = (e) => {
      // Only close if closeOnOutsideClick is true
      if (props.closeOnOutsideClick) {
        // Check if the click target is the backdrop itself or the flex container
        // (not the modal content or its children)
        if (e.target === e.currentTarget || 
            e.target.classList.contains('flex')) {
          closeModal();
        }
      }
    };

    const closeModal = () => {
      // Remove the modal state from history if it exists
      if (window.history.state && window.history.state.modal) {
        window.history.back();
      }
      modalStore.close();
    };

    const addEventListeners = () => {
      window.addEventListener("keydown", handleEscape);
      window.addEventListener("popstate", handlePopState);
      document.body.style.overflow = "hidden";
    };

    const removeEventListeners = () => {
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("popstate", handlePopState);
      document.body.style.overflow = "";
    };

    watch(
      () => modalStore.isOpen,
      (isOpen) => {
        if (isOpen) {
          addEventListeners();
          // Add a history state when modal opens
          pushModalState();
        } else {
          removeEventListeners();
        }
      }
    );

    onMounted(() => {
      if (modalStore.isOpen) {
        addEventListeners();
        pushModalState();
      }
    });

    onBeforeUnmount(() => {
      removeEventListeners();
    });

    return {
      modalStore,
      modalContent,
      modalWidthClass,
      closeModal,
      handleBackdropClick,
    };
  },
};
</script>

<style scoped>
/* Modal backdrop animation */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
}

/* Modal content animation */
.modal-content-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-content-leave-active {
  transition: all 0.25s ease-in;
}

.modal-content-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(-20px);
}

.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.modal-content-enter-to,
.modal-content-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
